// IMPORTS

// ANGULAR CORE MODULES
import { Injectable } from "@angular/core";
import { appSetting } from "config/appSettings";


// EXTERNAL LIBRARIES
import cavClient from 'libs-cavalier-integration/frontend';
import { customAlphabet, nanoid } from "nanoid";

const custNanoId = customAlphabet('1234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ', 10);

@Injectable()
export class EventService {

    // CONSTANTS
    CONSTANTS = {
        USER_ACTION_PERFORMED: 'UserActionPerformed',
        NETWORK_MODE: {
            ONLINE: 'online',
            OFFLINE: 'offline'
        },
        FEATURE: {
            ASSIGNMENT_CREATED: 'assignment-created'
        },
        SUB_SYSTEMS: {
            CambridgeOne: 'c1'
        },
        APP: 'learning-path'
    };

    // VARIABLES

    activityEvents = [];

    // Represents the current schema version
    schemaVersion = 1;

    // Represents the current user data
    userData = {
        extUserId: null,
        userRole: null,
        country: null
    }

    // Represents the last visited page
    lastVisitedPage;
    currentPage;

    // a unique session id generate everytime a user logs in by entering username and password
    loginSessionId;

    // a unique id generated in three conditions
    //  - If someone enters credentials online and logs in
    //  - If someone launches online and are automatically logged in in online mode
    //  - If someone launches offline and are automatically logged in in offline mode
    launchSessionId;

    constructor() {
        cavClient.init(this.CONSTANTS.APP);
    }

    // PUBLIC METHODS

    /**
     * Initializes the event service.
     * 
     * @param platform - The platform on which the service is being initialized.
     * @param userData - The user data containing userId, role, and country.
     * @param loginSessionId - The login session ID.
     * @returns The new login session ID.
     */
    init({ userId, role, country = undefined }, loginSessionId?) {
        this._setUserData(userId, role, country);
        const newLoginSessionId = this._updateLoginSessionId(loginSessionId);
        this._updateLaunchSessionId();

        return newLoginSessionId;
    }

    /**
     * Resets the user data and session information.
     */
    reset() {
        this._resetUserData();
        this._resetSessionInfo();
    }

    /**
     * Publishes an event based on the given type and data.
     * 
     * @param {string} type - The type of the event.
     * @param {any} data - The data associated with the event.
     * @returns {any} - The result of publishing the event.
     */
    publishEvent(type, data) {
        if(type === this.CONSTANTS.FEATURE.ASSIGNMENT_CREATED) {
            return this._publishAssignmentCreatedEvent(data);
        }
        console.warn("Error: Invalid event type");
    }

    // PRIVATE METHODS

    // EVENT PUBLISHING METHODS

    /**
     * Prepares the base event data object.
     * 
     * @returns The base event data object.
     */
    _prepareBaseEventData() {
        return {
            fact_id: `${this.userData.extUserId}~~${this._getPrefixedUniqueId(16, "")}`,
            fact_name: this.CONSTANTS.USER_ACTION_PERFORMED,
            timestamp: Date.now(),
            user_id: this.userData.extUserId,
            user_dls_id: this.userData.extUserId,
            user_role: this.userData.userRole,
            user_country: this.userData.country,
            session_id: this._getSessionId(),
            schema_version: this.schemaVersion,
            user_agent: window.navigator.userAgent,
            host: window.location.host,
            url: window.location.href,
            title: window.document.title,
            network_mode: this.CONSTANTS.NETWORK_MODE.ONLINE,
            sub_system: this.CONSTANTS.SUB_SYSTEMS.CambridgeOne,
            app: this.CONSTANTS.APP
        }
    }

    /**
     * Publishes a assignment created event.
     * 
     * @param {object} data - The data for the event.
     */
    _publishAssignmentCreatedEvent(data) {
        const eventData = {
            ...this._prepareBaseEventData(),
            feature_name: this.CONSTANTS.FEATURE.ASSIGNMENT_CREATED,
            organisation_id: data.organisationId,
            context: data.context,
            action_type: data.actionType
        };

        this._publishEvent(eventData);
    }

    // HELPER METHODS

    /**
     * Publishes an event and saves it in local storage before syncing with the server.
     * @param data - The event data to be published.
     */
    async _publishEvent(data) {
        try {
            // Add the new event data to the events array
            this.activityEvents.push(data);
            const events = JSON.parse(JSON.stringify(this.activityEvents));

            // If no events are present, then return
            if (!events.length) {
                return;
            }

            // syncing events data with cavalier
            const cavResponse = await cavClient.captureUserActionPerformedEvents(events);

            if (cavResponse) {
                // Handling the case, when events are syncing and new events are added
                // removing only those events, that were sent to sync
                this.activityEvents.splice(0, events.length);
            }
        } catch (error) {
            console.log("Error while syncing events", error);
        }
    }

    /**
     * Generates a unique ID with a given prefix.
     * 
     * @param {number} chars - The number of characters in the unique ID.
     * @param {string} prefix - The prefix to be added to the unique ID.
     * @returns {string} - The unique ID with the prefix.
     */
    _getPrefixedUniqueId(chars, prefix) {
        const uniqueId = nanoid(chars);
        return prefix + uniqueId;
    }

    // DATA METHODS

    /**
     * Sets the user data.
     * @param {string} uid - The user ID.
     * @param {string} userRole - The user role.
     * @param {string|null} country - The country (optional).
     */
    _setUserData(uid, userRole, country = null) {
        this.userData.extUserId = uid;
        this.userData.userRole = userRole;
        this.userData.country = country;
    }

    /**
     * Resets the user data and navigation properties.
     */
    _resetUserData() {
        this.userData = {
            extUserId: undefined,
            userRole: undefined,
            country: undefined
        };
        this.lastVisitedPage = undefined;
        this.currentPage = undefined;
    }

    /**
     * Gets the session ID by combining the login session ID and launch session ID.
     * If either login session ID or launch session ID is absent, it logs a warning and returns undefined.
     * @returns The combined session ID or undefined if either login session ID or launch session ID is absent.
     */
    _getSessionId() {
        if (this.loginSessionId && this.launchSessionId) {
            return `${this.loginSessionId}-${this.launchSessionId}`;
        } else {
            console.warn("Error: Login/Launch SessionId absent!");
            return;
        }
    }

    /**
     * Updates the login session ID.
     * 
     * @param sessionId - The session ID to update. If not provided, a new session ID will be generated.
     * @returns The updated login session ID.
     */
    _updateLoginSessionId(sessionId?) {
        this.loginSessionId = sessionId || custNanoId();
        return this.loginSessionId;
    }

    /**
     * Updates the launch session ID.
     * @returns The updated launch session ID.
     */
    _updateLaunchSessionId() {
        this.launchSessionId = custNanoId();
        return this.launchSessionId;
    }

    /**
     * Resets the session information by setting the loginSessionId and launchSessionId to null.
     */
    _resetSessionInfo() {
        this.loginSessionId = null;
        this.launchSessionId = null;
    }
}